/**
 * Goods-receipt composer — backs `/procurement/goods-receipts/new` and
 * `/procurement/goods-receipts/$grnId/edit` (draft editing).
 *
 * Faithful React reconstruction of `prototype/purchases/goods-receipt-new.html`.
 * The layout maps section by section to the prototype:
 *   - composer-head : back arrow + "Record goods receipt" + GRN number pill +
 *                     "View purchase order" action (`.composer-head`).
 *   - section 1     : Receipt details — Against purchase order* (live picker,
 *                     receivable POs only) · GRN number (auto) · Received date* ·
 *                     Vehicle/challan no. · Received by (`.field-grid`).
 *   - section 2     : Vendor — auto-populated from the PO (read-only card +
 *                     history strip).
 *   - section 3     : Received quantities — table (Item / Ordered / Received /
 *                     Accepted / Rejected-short / Reason) + `.ai-hint`.
 *   - section 4     : Inspection notes — textarea.
 *   - right rail    : live "What happens on post" preview (`.composer-preview`).
 *   - composer-foot : ready pill + status line + Save draft / Post goods receipt.
 *
 * A GRN is recorded *against a PO*. Selecting a PO loads `purchaseOrders.get`
 * and pre-fills one line per PO line (Ordered = qtyMilli; Received defaults to
 * ordered; Accepted defaults to received). "Save draft" persists the receipt
 * unposted (`createDraft` / `update`); "Post" finalises it (`record` for a
 * fresh receipt, `update`-then-`post` for a draft), which flips the PO + drafts
 * a matching bill. Both then navigate to the GRN detail.
 *
 * **Edit mode** (a `grnId` prop) loads the draft and merges its stored received
 * / accepted / reject values onto the PO's full line set — so every PO line is
 * editable again, with the draft's progress preserved. The PO can't be
 * repointed on a draft, so the picker is locked.
 *
 * The RHF wiring mirrors the PO + bill composers verbatim — including the
 * `form.setValue(field, value as never, ...)` patcher cast and the known
 * `zodResolver` exactOptionalPropertyTypes baseline error (not a fix-it site).
 *
 * @see prototype/purchases/goods-receipt-new.html — V1.1 composer baseline
 * @see packages/api-contract/src/procedures/goods-receipts.ts
 */
import { useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ExternalLink, Loader2, Save } from "lucide-react";
import {
  Alert,
  Button,
  Form,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { paiseToInr } from "../../../lib/format.js";
import {
  EMPTY_GRN_FORM,
  grnFormSchema,
  type GrnFormValues,
  type GrnLineRow,
} from "../lib/form-schema.js";
import { computeGrnTotals, hasReceivedQty, milliToInput } from "../lib/grn-math.js";
import { GrnField, GrnFieldGrid, GrnSectionCard } from "./grn-section-card.js";
import { GrnVendorSection, type GrnVendor } from "./grn-vendor-section.js";
import { GrnReceivedLines } from "./grn-received-lines.js";
import { GrnPreview } from "./grn-preview.js";

/** PO statuses that can still receive goods (the picker filter). */
const RECEIVABLE = new Set(["sent", "confirmed", "partially_received"]);

/**
 * Swap raw domain error messages (which embed internal UUIDs) for plain-English,
 * actionable copy for the composer's mutation errors. Mirrors the
 * `describeVoidError` mapper on the detail page.
 * @see packages/domain/src/purchases/grns/errors.ts
 */
function describeGrnMutationError(err: {
  data?: { code?: string | undefined } | undefined | null;
  message?: string | undefined | null;
} | null | undefined): string {
  if (!err) return "";
  const msg = err.message ?? "";
  if (/This looks like a duplicate of a goods receipt you just recorded/i.test(msg)) {
    return "This looks like a duplicate of a receipt you just posted. Refresh to see the one already on file.";
  }
  if (/cannot authorise posting this goods receipt \(payload_mismatch\)/i.test(msg)) {
    return "The receipt lines changed since this was approved. Re-request approval before posting.";
  }
  if (/cannot authorise posting this goods receipt \(not_found\)/i.test(msg)) {
    return "The approval this receipt was waiting on no longer exists. Re-request approval and try again.";
  }
  if (/cannot authorise posting this goods receipt \(not_approved\)/i.test(msg)) {
    return "This receipt hasn't been approved yet. Wait for sign-off before posting.";
  }
  if (/cannot authorise posting this goods receipt \(wrong_kind\)/i.test(msg)) {
    return "The linked approval doesn't cover posting goods receipts.";
  }
  if (/exceeds the remaining balance/i.test(msg)) {
    return "One or more received quantities exceed the PO's remaining balance. Reduce the received quantity.";
  }
  if (/Duplicate PO line reference/i.test(msg)) {
    return "The same PO line appears more than once. Remove the duplicate row.";
  }
  if (/Reject reason required on PO line/i.test(msg)) {
    return "Enter a reject reason for every line where the rejected quantity is greater than zero.";
  }
  if (/Invalid quantity on PO line/i.test(msg)) {
    return "One or more quantities are invalid. Accepted must be ≤ received, and no value may be negative.";
  }
  if (/A goods receipt must record at least one/i.test(msg)) {
    return "Enter a received quantity on at least one line before posting.";
  }
  if (/references PO line .*, which is not on this PO/i.test(msg)) {
    return "One of the receipt lines doesn't match the selected purchase order. Reload and try again.";
  }
  if (/^Purchase order .+ is .+ and cannot receive goods/i.test(msg)) {
    return "This purchase order can no longer receive goods. Its status may have changed — refresh and pick a different PO.";
  }
  if (/^Purchase order .+ not found/i.test(msg)) {
    return "That purchase order no longer exists.";
  }
  if (/only drafts can be edited, posted, or deleted/i.test(msg)) {
    return "This goods receipt is no longer a draft. Refresh to see the current status.";
  }
  switch (err.data?.code) {
    case "PARSE_ERROR":
    case "BAD_REQUEST":
      return "Some of the entered details aren't valid. Check the highlighted rows and try again.";
    case "NOT_FOUND":
      return "The requested item no longer exists. Refresh and try again.";
    case "UNAUTHORIZED":
    case "FORBIDDEN":
      return "You don't have permission to complete this action.";
    default:
      return "Couldn't save this goods receipt. Try again in a moment.";
  }
}

export interface GrnComposerPageProps {
  /** Optional deep-link preselect — `/procurement/goods-receipts/new?po=<uuid>`. */
  preselectPoId?: string | undefined;
  /** When set, the composer edits this draft goods receipt. */
  grnId?: string | undefined;
}

export function GrnComposerPage({ preselectPoId, grnId }: GrnComposerPageProps = {}) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const isEdit = grnId !== undefined && grnId !== "";

  // Receivable POs for the picker. The list contract has no multi-status
  // filter, so we pull a page and filter client-side to RECEIVABLE.
  const poList = trpc.purchaseOrders.list.useQuery({ limit: 100 });
  const receivablePos = useMemo(
    () => (poList.data?.items ?? []).filter((po) => RECEIVABLE.has(po.status)),
    [poList.data?.items],
  );

  // Edit mode: load the draft GRN so we can hydrate the form from it.
  const editGrn = trpc.goodsReceipts.get.useQuery(
    { id: grnId ?? "" },
    { enabled: isEdit },
  );

  const form = useForm<GrnFormValues>({
    resolver: zodResolver(grnFormSchema),
    defaultValues: EMPTY_GRN_FORM,
    mode: "onBlur",
  });

  const values = useWatch({ control: form.control }) as GrnFormValues;
  const selectedPoId = values.purchaseOrderId || "";

  // Load the chosen PO's header + lines to pre-fill the table.
  const loadedPo = trpc.purchaseOrders.get.useQuery(
    { id: selectedPoId },
    { enabled: selectedPoId !== "" },
  );

  const invalidate = async () => {
    await Promise.all([
      utils.goodsReceipts.list.invalidate(),
      utils.purchaseOrders.list.invalidate(),
      ...(isEdit ? [utils.goodsReceipts.get.invalidate({ id: grnId! })] : []),
    ]);
  };

  const record = trpc.goodsReceipts.record.useMutation({ onSuccess: invalidate });
  const createDraft = trpc.goodsReceipts.createDraft.useMutation({ onSuccess: invalidate });
  const update = trpc.goodsReceipts.update.useMutation({ onSuccess: invalidate });
  const post = trpc.goodsReceipts.post.useMutation({ onSuccess: invalidate });

  const warehouseList = trpc.warehouses.list.useQuery({ includeInactive: false });
  const warehouses = warehouseList.data?.warehouses ?? [];

  // Edit mode: only drafts are editable (append-only rule — posted / void GRNs
  // are terminal). Redirect back to the detail page if a non-draft slips in via
  // the URL — the composer must never mount for a non-draft GRN.
  const nonDraftEdit = isEdit && editGrn.data !== undefined && editGrn.data.status !== "draft";
  useEffect(() => {
    if (!nonDraftEdit || !grnId) return;
    void navigate({ to: "/procurement/goods-receipts/$grnId", params: { grnId }, replace: true });
  }, [nonDraftEdit, grnId, navigate]);

  // Edit mode: seed the PO id + header fields from the draft (once). Setting
  // the PO id triggers `loadedPo`, after which the line-hydration effect runs.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!isEdit || !editGrn.data || seededRef.current) return;
    if (editGrn.data.status !== "draft") return; // guard covers the render, this covers the seed
    seededRef.current = true;
    const g = editGrn.data;
    form.setValue("purchaseOrderId", g.purchaseOrderId, { shouldDirty: false });
    form.setValue("receivedDate", g.receivedDate, { shouldDirty: false });
    form.setValue("vehicleChallan", g.vehicleChallan ?? "", { shouldDirty: false });
    form.setValue("receivedBy", g.receivedBy ?? "", { shouldDirty: false });
    form.setValue("notes", g.notes ?? "", { shouldDirty: false });
    form.setValue("warehouseId", g.warehouseId ?? "", { shouldDirty: false });
  }, [isEdit, editGrn.data, form]);

  // Apply the deep-link preselect once, when its PO is present in the list.
  const preselectedRef = useRef(false);
  useEffect(() => {
    if (isEdit || preselectedRef.current || !preselectPoId) return;
    if (receivablePos.some((po) => po.id === preselectPoId)) {
      preselectedRef.current = true;
      form.setValue("purchaseOrderId", preselectPoId, { shouldDirty: true });
    }
  }, [isEdit, preselectPoId, receivablePos, form]);

  // When the loaded PO changes, (re)build the received-quantities rows. In edit
  // mode we overlay the draft's stored received/accepted/reject onto each
  // matching PO line, so the full PO line set is editable with progress kept.
  const hydratedPoRef = useRef<string | null>(null);
  useEffect(() => {
    if (!loadedPo.data) return;
    if (hydratedPoRef.current === loadedPo.data.id) return;
    if (isEdit && !editGrn.data) return; // wait for the draft before overlaying
    hydratedPoRef.current = loadedPo.data.id;
    const stored = new Map((editGrn.data?.lines ?? []).map((l) => [l.purchaseOrderLineId, l]));
    const rows: GrnLineRow[] = loadedPo.data.lineItems.map((line) => {
      const orderedInput = milliToInput(line.qtyMilli);
      const s = stored.get(line.id);
      return {
        purchaseOrderLineId: line.id,
        description: line.description,
        hsn: line.hsn,
        unit: line.unit,
        ordered: orderedInput,
        ratePaise: line.ratePaise,
        received: s ? milliToInput(s.receivedQtyMilli) : orderedInput,
        accepted: s ? milliToInput(s.acceptedQtyMilli) : orderedInput,
        rejectReason: s?.rejectReason ?? "",
      };
    });
    form.setValue("lines", rows, { shouldDirty: true });
  }, [loadedPo.data, editGrn.data, isEdit, form]);

  const lines = values.lines ?? [];
  const totals = computeGrnTotals(lines);
  const errors = form.formState.errors;

  const selectedPo = receivablePos.find((po) => po.id === selectedPoId) ?? null;
  const vendor: GrnVendor | null = loadedPo.data
    ? {
        name: loadedPo.data.vendorName,
        gstin: loadedPo.data.vendorGstin,
        state: loadedPo.data.vendorState,
        city: loadedPo.data.vendorCity,
      }
    : selectedPo
      ? { name: selectedPo.vendorName, gstin: selectedPo.vendorGstin, state: selectedPo.vendorState, city: selectedPo.vendorCity }
      : editGrn.data
        ? { name: editGrn.data.vendorName, gstin: editGrn.data.vendorGstin, state: editGrn.data.vendorState, city: editGrn.data.vendorCity }
        : null;
  const poNumber = loadedPo.data?.poNumber ?? selectedPo?.poNumber ?? editGrn.data?.poNumber ?? null;

  const patch = <K extends keyof GrnFormValues>(field: K, value: GrnFormValues[K]) => {
    form.setValue(field, value as never, { shouldDirty: true, shouldTouch: true });
    form.clearErrors(field);
  };

  const ready = selectedPoId !== "" && !!values.receivedDate && lines.some(hasReceivedQty);
  const pending =
    record.isPending || createDraft.isPending || update.isPending || post.isPending;

  /** Map the form's recordable lines (received > 0) onto the wire shape. */
  const recordableLines = (parsed: GrnFormValues) =>
    parsed.lines.filter(hasReceivedQty).map((line) => ({
      purchaseOrderLineId: line.purchaseOrderLineId,
      receivedQty: line.received.trim(),
      acceptedQty: line.accepted.trim(),
      rejectReason: line.rejectReason.trim() || null,
    }));

  const goToDetail = (id: string) =>
    void navigate({ to: "/procurement/goods-receipts/$grnId", params: { grnId: id } });

  const submitWith = (mode: "draft" | "post") =>
    form.handleSubmit(async (parsed) => {
      const recordable = recordableLines(parsed);
      if (recordable.length === 0) {
        form.setError("lines", {
          type: "manual",
          message: "Enter a received quantity on at least one line.",
        });
        return;
      }
      const header = {
        receivedDate: parsed.receivedDate,
        vehicleChallan: parsed.vehicleChallan.trim() || null,
        receivedBy: parsed.receivedBy.trim() || null,
        notes: parsed.notes.trim() || null,
        warehouseId: parsed.warehouseId.trim() || null,
        lines: recordable,
      };
      try {
        if (mode === "draft") {
          if (isEdit) {
            await update.mutateAsync({ id: grnId!, ...header });
            toast.success("Draft saved", { description: "Your changes to this goods receipt are saved." });
            goToDetail(grnId!);
          } else {
            const result = await createDraft.mutateAsync({
              purchaseOrderId: parsed.purchaseOrderId,
              ...header,
            });
            toast.success("Draft goods receipt saved", {
              description: "Come back to post it when inspection is done.",
            });
            goToDetail(result.id);
          }
          return;
        }
        // mode === "post"
        if (isEdit) {
          await update.mutateAsync({ id: grnId!, ...header });
          const result = await post.mutateAsync({ id: grnId! });
          toast.success(`Goods receipt ${result.grnNumber} posted`, {
            description: result.billId
              ? "PO fulfilment updated and a draft matching bill was created."
              : "PO fulfilment updated.",
          });
          goToDetail(grnId!);
        } else {
          const result = await record.mutateAsync({
            purchaseOrderId: parsed.purchaseOrderId,
            ...header,
          });
          if (result.outcome === "awaiting_approval" || result.id === null) {
            // A configured approval chain (ADR-0031) gated the post — nothing
            // was inserted. Stay on the composer; the receipt posts on resubmit
            // once the chain clears.
            toast.success("Sent for approval", {
              description: "This goods receipt needs sign-off before it's posted.",
            });
            await invalidate();
            return;
          }
          toast.success(`Goods receipt ${result.grnNumber} posted`, {
            description: result.billId
              ? "PO fulfilment updated and a draft matching bill was created."
              : "PO fulfilment updated.",
          });
          goToDetail(result.id);
        }
      } catch (err) {
        toast.error(mode === "draft" ? "Couldn't save draft" : "Couldn't post goods receipt", {
          description: describeGrnMutationError(err as { data?: { code?: string }; message?: string }),
        });
      }
    });

  const mutationError = record.error ?? createDraft.error ?? update.error ?? post.error;
  const grnNumberLabel = isEdit
    ? (editGrn.data?.grnNumber ?? "Draft · auto on post")
    : "Auto-assigned on post";

  // Non-draft edit: the redirect effect above will bounce to detail. Render a
  // neutral placeholder so nothing flashes.
  if (nonDraftEdit) {
    return <p className="p-6 text-sm text-muted">Opening goods receipt…</p>;
  }

  return (
    <Form {...form}>
      <form onSubmit={submitWith("post")} className="flex min-h-full flex-col bg-bg">
        {/* ── Composer head — sticky band (.composer-head) ─────────────── */}
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3.5 border-b border-line bg-paper px-6">
          <Link
            to="/procurement/goods-receipts"
            aria-label="Back to goods receipts"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-line text-ink-2 transition hover:bg-bg"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              aria-hidden="true"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="flex items-center gap-2 text-base font-bold leading-tight tracking-tight text-ink">
              {isEdit ? "Edit goods receipt" : "Record goods receipt"}
              <span className="rounded-full bg-raised px-2 py-0.5 font-mono text-xs font-medium text-muted">
                {isEdit ? (editGrn.data?.grnNumber ?? "Draft") : "GRN · auto"}
              </span>
            </h1>
            <p className="mt-0.5 text-xs text-muted">
              Record what arrived against a purchase order — accept or reject per line.
            </p>
          </div>
          {selectedPoId ? (
            <Button asChild intent="ghost" className="border border-line bg-paper">
              <Link to="/procurement/purchase-orders/$poId" params={{ poId: selectedPoId }}>
                <ExternalLink size={13} />
                View purchase order
              </Link>
            </Button>
          ) : null}
        </header>

        {/* ── Two-column body (.composer-body) ─────────────────────────── */}
        <main className="mx-auto grid w-full max-w-[1400px] flex-1 gap-5 px-6 pb-28 pt-5 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          <div className="flex min-w-0 flex-col gap-4">
            {poList.error ? (
              <Alert
                intent="bad"
                title="Couldn't load purchase orders"
                description={poList.error.message}
              />
            ) : null}
            {isEdit && editGrn.error ? (
              <Alert
                intent="bad"
                title="Couldn't load draft goods receipt"
                description={editGrn.error.message}
              />
            ) : null}
            {mutationError ? (
              <Alert
                intent="bad"
                title="Couldn't save goods receipt"
                description={describeGrnMutationError(mutationError)}
              />
            ) : null}

            {/* 1 · Receipt details */}
            <GrnSectionCard
              step="1"
              title="Receipt details"
              description="Against which purchase order was this delivery made? All lines are pre-filled from the PO."
            >
              <GrnFieldGrid cols={2} className="mb-3.5">
                <GrnField
                  id="grn-po"
                  label="Against purchase order"
                  required
                  error={errors.purchaseOrderId?.message}
                  help={
                    selectedPoId ? (
                      <Link
                        to="/procurement/purchase-orders/$poId"
                        params={{ poId: selectedPoId }}
                        className="font-medium text-sky-700 hover:underline"
                      >
                        View purchase order →
                      </Link>
                    ) : (
                      "Only sent / confirmed / partially-received orders can receive goods."
                    )
                  }
                >
                  <Select
                    value={selectedPoId}
                    onValueChange={(v) => patch("purchaseOrderId", v)}
                    disabled={poList.isLoading || pending || isEdit}
                  >
                    <SelectTrigger id="grn-po" className="h-9 min-w-0 overflow-hidden text-sm">
                      <SelectValue
                        placeholder={poList.isLoading ? "Loading…" : "Select purchase order"}
                        className="block min-w-0 truncate"
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {receivablePos.length === 0 ? (
                        <div className="px-2 py-3 text-center text-xs text-muted">
                          No receivable purchase orders.
                        </div>
                      ) : (
                        receivablePos.map((po) => (
                          <SelectItem key={po.id} value={po.id} className="max-w-80">
                            <span className="block truncate">
                              {po.poNumber ?? "Draft"} · {po.vendorName}
                            </span>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </GrnField>
                <GrnField id="grn-number" label="GRN number" help={isEdit ? "Assigned on post" : "Auto-assigned on post"}>
                  <Input
                    id="grn-number"
                    value={grnNumberLabel}
                    readOnly
                    disabled
                    className="h-9 font-mono text-sm"
                  />
                </GrnField>
              </GrnFieldGrid>
              <GrnFieldGrid cols={3}>
                <GrnField
                  id="grn-received-date"
                  label="Received date"
                  required
                  error={errors.receivedDate?.message}
                  help="Date goods arrived at your premises"
                >
                  <Input
                    id="grn-received-date"
                    type="date"
                    value={values.receivedDate || ""}
                    onChange={(e) => patch("receivedDate", e.currentTarget.value)}
                    disabled={pending}
                    className="h-9 font-mono text-sm"
                  />
                </GrnField>
                <GrnField
                  id="grn-challan"
                  label="Vehicle / challan no."
                  help="Delivery note or lorry receipt number"
                >
                  <Input
                    id="grn-challan"
                    value={values.vehicleChallan || ""}
                    onChange={(e) => patch("vehicleChallan", e.currentTarget.value)}
                    placeholder="e.g. MH-12 AB 4321 or DC-7801"
                    disabled={pending}
                    className="h-9 text-sm"
                  />
                </GrnField>
                <GrnField id="grn-received-by" label="Received by" help="Person who accepted the delivery">
                  <Input
                    id="grn-received-by"
                    value={values.receivedBy || ""}
                    onChange={(e) => patch("receivedBy", e.currentTarget.value)}
                    placeholder="e.g. Storekeeper"
                    disabled={pending}
                    className="h-9 text-sm"
                  />
                </GrnField>
                {warehouses.length > 0 && (
                  <GrnField
                    id="grn-warehouse"
                    label="Receive into warehouse"
                    help="Defaults to company default if left blank"
                  >
                    <Select
                      value={values.warehouseId || "__default__"}
                      onValueChange={(v) => patch("warehouseId", v === "__default__" ? "" : v)}
                      disabled={pending}
                    >
                      <SelectTrigger className="!h-9 !text-sm w-full">
                        <SelectValue placeholder="Company default" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__default__">Company default</SelectItem>
                        {warehouses.map((wh) => (
                          <SelectItem key={wh.id} value={wh.id}>
                            {wh.name}{wh.isDefault ? " (default)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </GrnField>
                )}
              </GrnFieldGrid>
            </GrnSectionCard>

            {/* 2 · Vendor */}
            <GrnVendorSection poNumber={poNumber} vendor={vendor} />

            {/* 3 · Received quantities */}
            <GrnReceivedLines
              rows={lines}
              poNumber={poNumber}
              vendorName={vendor?.name ?? null}
              disabled={pending}
              error={errors.lines?.message ?? errors.lines?.root?.message}
              onRowsChange={(rows) => patch("lines", rows)}
            />

            {/* 4 · Inspection notes */}
            <GrnSectionCard
              step="4"
              title="Inspection notes"
              description="Any quality, packaging, or handling observations for this delivery."
            >
              <GrnField id="grn-notes" label="Notes" help="Visible to the storekeeper and the operator. Not sent to vendor.">
                <textarea
                  id="grn-notes"
                  rows={4}
                  value={values.notes || ""}
                  onChange={(e) => patch("notes", e.currentTarget.value)}
                  placeholder="e.g. Outer cartons dented on 2 boxes; inner goods intact."
                  disabled={pending}
                  className="min-h-[88px] w-full resize-y rounded-sm border border-line bg-paper px-3 py-2 text-sm leading-relaxed text-ink outline-none transition-colors placeholder:text-muted hover:border-line-2 focus-visible:border-sky-600 focus-visible:ring-2 focus-visible:ring-sky-100 disabled:opacity-60"
                />
              </GrnField>
            </GrnSectionCard>
          </div>

          {/* ── Right column · live "what happens on post" preview ───────── */}
          <GrnPreview
            grnNumber={isEdit ? (editGrn.data?.grnNumber ?? "Draft GRN") : "Draft GRN"}
            poNumber={poNumber}
            vendor={vendor}
            receivedDate={values.receivedDate || ""}
            receivedBy={values.receivedBy || ""}
            lines={lines}
          />
        </main>

        {/* ── Sticky footer (.composer-foot) ───────────────────────────── */}
        <footer className="sticky bottom-0 z-20 border-t border-line bg-paper px-6 py-3 shadow-card">
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-3">
            <span
              className={[
                "inline-flex h-7 shrink-0 items-center gap-1 rounded-full px-3 text-xs font-semibold",
                ready ? "bg-emerald-50 text-emerald-700" : "bg-warn-50 text-warn",
              ].join(" ")}
            >
              <Check size={13} aria-hidden />
              {ready ? "Ready to post" : "Pick a PO and a received quantity"}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-muted">
              {poNumber ? `${poNumber} · ` : ""}
              {totals.acceptedLineCount} of {totals.totalLineCount} line
              {totals.totalLineCount === 1 ? "" : "s"} accepted
              {totals.rejectedMilli > 0n ? " · shortage recorded" : ""} · draft matching{" "}
              <strong className="font-mono text-ink">{paiseToInr(totals.matchingPaise)}</strong>
            </span>
            <Button type="button" intent="ghost" onClick={() => void navigate({ to: "/procurement/goods-receipts" })}>
              Cancel
            </Button>
            <Button
              type="button"
              intent="ghost"
              className="border border-line bg-paper"
              disabled={pending || !ready}
              onClick={submitWith("draft")}
            >
              {createDraft.isPending || (update.isPending && !post.isPending) ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Save size={15} />
              )}
              Save draft
            </Button>
            <Button type="submit" disabled={pending || !ready}>
              {post.isPending || record.isPending ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Check size={15} />
              )}
              Post goods receipt
            </Button>
          </div>
        </footer>
      </form>
    </Form>
  );
}
